import { useMemo, useCallback, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShieldExclamationIcon } from '@heroicons/react/24/outline'
import { useWorkspaceDetails } from '@/features/workspace/hooks/useWorkspaceDetails'
import { useActiveWorkspace } from '@/state/selection.store'
import { PermissionGuard } from '@/app/router/guards'
import { PERMISSIONS } from '@/lib/permissions'
import { getLatestPlan } from '@/features/usage/utils'
import { Button } from '@/components/catalyst-ui/button'
import { GrowthPlanPromoCard } from '@/components/ui/GrowthPlanPromoCard'
import { hasPaidWorkspacePlan } from '@/lib/appEntitlements'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatResetDate = (date) => {
  if (!date) return ''
  try {
    const d = new Date(date)
    return `${MONTHS[d.getMonth()]} ${d.getDate()}`
  } catch {
    return ''
  }
}

const parseBillingDate = (nextBillingDate) => {
  if (!nextBillingDate) return null
  return nextBillingDate.$date ? new Date(nextBillingDate.$date) : new Date(nextBillingDate)
}

const getResetDate = (aiCredits, plans) => {
  const expiryOn = aiCredits?.currentCredit?.expiryOn
  if (expiryOn) return new Date(expiryOn)
  const latestPlan = getLatestPlan(plans || [])
  return parseBillingDate(latestPlan?.nextBillingDate)
}

export const AICreditsCard = memo(function AICreditsCard({ className = '', suppressFreeWorkspacePromo = false }) {
  const { workspaceId } = useActiveWorkspace()
  const { workspace, isLoading } = useWorkspaceDetails({ workspaceId })
  const navigate = useNavigate()

  const aiCredits = workspace?.aiCredits
  const plans = workspace?.plans
  const hasPaidWorkspace = hasPaidWorkspacePlan(workspace)
  const shouldHideCredits = Boolean(workspace?.hideCredits)

  const { total, used, percentUsed, isExhausted, resetDate } = useMemo(() => {
    const totalCredits = aiCredits?.totalCredits ?? 0
    const usedCredits = aiCredits?.usedCredits ?? 0

    if (totalCredits === 0) {
      return {
        total: 0,
        used: 0,
        percentUsed: 0,
        isExhausted: false,
        resetDate: null,
      }
    }

    const pct = Math.min(100, Math.round((usedCredits / totalCredits) * 100))
    const exhausted = usedCredits >= totalCredits
    const resetDateObj = getResetDate(aiCredits, plans)

    return {
      total: totalCredits,
      used: usedCredits,
      percentUsed: pct,
      isExhausted: exhausted,
      resetDate: resetDateObj,
    }
  }, [aiCredits, plans])

  const formattedResetDate = useMemo(() => formatResetDate(resetDate), [resetDate])

  const handleNavigate = useCallback(() => {
    const targetWorkspace = workspace?._id || workspaceId || 'default'
    navigate(`/usage?workspace=${targetWorkspace}`)
  }, [workspace?._id, workspaceId, navigate])

  const handleUpgrade = useCallback(() => {
    const targetWorkspace = workspace?._id || workspaceId || ''
    navigate(`/pricing?workspace=${encodeURIComponent(targetWorkspace)}&feature=ai_credits_card`)
  }, [navigate, workspace?._id, workspaceId])

  if (shouldHideCredits) {
    return null
  }

  // Show loading skeleton while data is being fetched
  if (isLoading) {
    return (
      <div className={className}>
        <div className="bg-white rounded-2xl p-3 border border-[#E4E4E7] animate-pulse">
          <div className="flex items-center justify-between mb-2">
            <div className="h-4 w-20 bg-gray-200 rounded" />
            <div className="h-3 w-16 bg-gray-200 rounded" />
          </div>
          <div className="mb-4">
            <div className="w-full h-[6px] bg-gray-200 rounded-full" />
          </div>
          <div className="h-8 w-full bg-gray-200 rounded-lg" />
        </div>
      </div>
    )
  }

  if (total === 0 && !hasPaidWorkspace) {
    if (suppressFreeWorkspacePromo) return null

    const targetWorkspace = workspace?._id || workspaceId || 'default'
    return <GrowthPlanPromoCard className={className} workspaceId={targetWorkspace} />
  }

  if (total === 0) {
    return (
      <div className={className}>
        <div className="bg-white rounded-2xl p-4 border border-[#E4E4E7]">
          <div className="flex items-center gap-2 mb-2">
            <ShieldExclamationIcon className="w-5 h-5 text-[#09090B] flex-shrink-0" />
            <h3 className="text-sm font-semibold text-[#09090B]">
              No AI credits remaining
            </h3>
          </div>
          <p className="text-xs font-medium text-[#71717A] mb-4">
            To continue making AI edits, please add more credits or upgrade your plan.
          </p>
          <PermissionGuard permission={PERMISSIONS.UPGRADE_NOW}>
            <Button
              plain
              onClick={handleUpgrade}
              className="w-full cursor-pointer !text-base !font-semibold !bg-[#5C33FF] !text-white hover:!bg-[#5855EB] data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
            >
              Upgrade Now
            </Button>
          </PermissionGuard>
        </div>
      </div>
    )
  }

  if (isExhausted) {
    return (
      <div className={className}>
        <div className="bg-white rounded-2xl p-3 border border-[#E4E4E7]">
          <div className="flex items-center justify-between mb-1">
            <div className="w-6 h-6 text-[#B91C1C] flex-shrink-0">
              <ShieldExclamationIcon className="w-5 h-5" />
            </div>
            <h3 className="text-xs font-medium text-[#71717A]">
              <span className="text-[#141414]">{used}</span> of <span className="text-[#71717A]">{total}</span> used
            </h3>
          </div>
          <h3 className="text-sm font-semibold text-[#B91C1C] mb-2">
            You&apos;ve used all your monthly AI credits
          </h3>
          <p className="text-xs text-[#71717A] font-medium mb-4">
            To continue making AI edits, please upgrade your plan.{' '}
            {formattedResetDate && `Your credit reset in ${formattedResetDate}. `}
            <button onClick={handleNavigate} className="underline text-[#71717A] hover:text-[#52525B]">
              view usage
            </button>
          </p>
          <PermissionGuard permission={PERMISSIONS.UPGRADE_NOW}>
            <Button
              plain
              onClick={handleUpgrade}
              className="w-full cursor-pointer !text-base !font-semibold !bg-[#5C33FF] !text-white hover:!bg-[#5855EB] data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
            >
              Upgrade Plan
            </Button>
          </PermissionGuard>
        </div>
      </div>
    )
  }

  return (
    <div className={className}>
      <div className="bg-white rounded-2xl p-3 border border-[#E4E4E7]">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-[#09090B]">AI Credits</h3>
          <span className="text-xs font-medium text-[#6B7280]">
            {isLoading ? 'Loading…' : `${percentUsed}% used`}
          </span>
        </div>
        <div className="mb-4">
          <div className="w-full h-[6px] bg-[#E5E7EB] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#5C33FF] rounded-full transition-all duration-300"
              style={{ width: `${percentUsed}%` }}
            />
          </div>
        </div>
        <PermissionGuard permission={PERMISSIONS.UPGRADE_NOW}>
          <button
            className="w-full bg-black text-white text-sm font-semibold py-1.5 rounded-lg hover:bg-gray-900 transition-colors"
            onClick={handleNavigate}
            disabled={isLoading}
          >
            Add more AI credits
          </button>
        </PermissionGuard>
      </div>
    </div>
  )
})
